import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmResetPassword } from 'aws-amplify/auth';
import FlowRCMLogo from '../components/FlowRCMLogo';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !code || !password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setLoading(true);

      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: password
      });

      setSuccessMessage('Password updated successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      const msg = error.message || '';
      if (msg.includes('CodeMismatchException') || msg.includes('Invalid verification code')) {
        setError('Invalid verification code. Please check and try again.');
      } else if (msg.includes('ExpiredCodeException')) {
        setError('Verification code has expired. Please request a new one.');
      } else if (msg.includes('InvalidPasswordException') || msg.includes('Password did not conform')) {
        setError('Password must include uppercase, lowercase, number, and special character.');
      } else if (msg.includes('LimitExceededException')) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError(error.message || 'Failed to update password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-4">
          <div className="flex justify-center mb-4">
            <FlowRCMLogo variant="light" hideSubtitle={true} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Set New Password</h1>
          <p className="text-slate-600 mt-2">Enter the verification code from your email</p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-8" style={{boxShadow: '0 0 10px rgba(0,0,0,0.1)'}}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {successMessage && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                {successMessage}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="you@dentalclinic.com"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="code" className="block text-sm font-semibold text-slate-700 mb-2">
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all text-center text-lg tracking-widest"
                placeholder="123456"
                disabled={loading}
                maxLength={6}
              />
              <p className="text-xs text-slate-500 mt-1">Check your email for the 6-digit code</p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-slate-700 mb-2">
                New Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                disabled={loading}
              />
              <p className="text-xs text-slate-500 mt-1">Must be at least 8 characters with uppercase, lowercase, number, and special character</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-slate-700 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                placeholder="••••••••"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 text-white py-3 rounded-lg font-semibold hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                'Update Password'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <a href="/forgot-password" className="text-teal-600 hover:text-teal-700 font-semibold text-sm">
              Didn't receive a code? Send again
            </a>
          </div>
        </div>

        <div className="mt-4 text-center text-sm text-slate-500">
          <p>&copy; 2026 FlowRCM. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
